// Le skin porté, dès l'ouverture (D-149) : la feuille de style du skin de la joueuse est gardée sur l'appareil (son club,
// sa clé, clair ou sombre, la feuille) et posée avant le premier affichage, sans attendre la base ni les dessins (chargés à
// la demande, src/ui/skins/). Le skin ne vaut que pour l'app de la joueuse : la casquette Joueuse (et Bénévole) ; une
// casquette de coach ou de bureau garde les couleurs du club. Clair ou sombre : celui du skin ; sans skin, le réglage
// « Apparence » de Moi reprend la main.
import { applyAppearance, readAppearance } from './appearance';

const KEY = 'arceau.skin.v1';
export interface SavedSkin { uid: string; club: string; key: string; scheme: 'light' | 'dark'; css: string }

let saved: SavedSkin | null = null;
let shown = false;

function read(): SavedSkin | null {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) ?? 'null') as SavedSkin | null;
    return s && typeof s.key === 'string' && typeof s.css === 'string' && typeof s.club === 'string' && typeof s.uid === 'string' ? s : null;
  } catch { return null; }
}

function styleEl(): HTMLStyleElement {
  let st = document.getElementById('sk-worn') as HTMLStyleElement | null;
  if (!st) { st = document.createElement('style'); st.id = 'sk-worn'; document.head.append(st); }
  return st;
}

/** Pose (ou retire) le skin gardé, selon qu'il doit se voir maintenant. */
function paint(on: boolean): void {
  const root = document.documentElement;
  if (on && saved) {
    const st = styleEl();
    if (st.textContent !== saved.css) st.textContent = saved.css;
    root.dataset.skin = saved.key;
    applyAppearance(saved.scheme);
  } else {
    delete root.dataset.skin;
    if (shown) applyAppearance(readAppearance());
  }
  shown = on && !!saved;
}

/** Au démarrage, avant le premier affichage : le skin gardé pour ce compte et ce club (le dernier ouvert sur l'appareil) ;
 *  un autre compte sur le même téléphone ne le voit jamais. */
export function bootSkin(clubId: string | null, uid: string | null): void {
  saved = read();
  if (saved && saved.uid !== uid) saved = null;
  paint(!!saved && saved.club === clubId);
}

/** Le skin que la base vient de dire (null : aucun, module éteint, autre club) ; gardé pour la prochaine ouverture. */
export function saveSkin(s: SavedSkin | null): void {
  saved = s;
  try { if (s) localStorage.setItem(KEY, JSON.stringify(s)); else localStorage.removeItem(KEY); } catch { /* navigation privée */ }
}

/** Le skin se voit-il sur cet écran ? (le club affiché, la casquette de la joueuse). */
export function showSkin(clubId: string | null, on: boolean): void {
  paint(on && !!saved && saved.club === clubId);
}

/** Le skin affiché en ce moment (sa clé), ou null. */
export const shownSkin = (): string | null => (shown && saved ? saved.key : null);
/** Le skin gardé pour ce club, même s'il n'est pas affiché. */
export const savedSkin = (clubId: string | null): SavedSkin | null => (saved && saved.club === clubId ? saved : null);

/** Un essai (« Essayer 10 secondes ») : le skin posé le temps de le voir, sans rien garder. */
export function tryCss(key: string, scheme: 'light' | 'dark', css: string): () => void {
  const before = { saved, shown };
  saved = { uid: '', club: '', key, scheme, css };
  const st = styleEl(); const prev = st.textContent;
  st.textContent = css;
  document.documentElement.dataset.skin = key;
  applyAppearance(scheme);
  shown = true;
  return () => {
    saved = before.saved;
    st.textContent = prev;
    shown = false;
    paint(before.shown);
  };
}
